import { defineStore } from 'pinia'
import { useProductStore } from './product'
import { OFFERS_LIMIT } from '~/constants/common'
import { buildQueryString } from '~/utils/http'
import type { ProductOfferData, ProductOffersCondition, ProductOffersMeta } from '~/types/product'
import type { QueryParamsList } from '~/types/query'
import type { ServerListResponse } from '~/types/api/response.types'

interface State {
  fetching: boolean
  offersData: ProductOfferData[]
  offersConditions: ProductOffersCondition[]
  offersCount: number
  meta: ProductOffersMeta
}

export const useProductOffersStore = defineStore('productOffers', {
  state: (): State => ({
    fetching: false,
    offersData: [],
    offersConditions: [],
    offersCount: 0,
    meta: {
      condition_key: {
        items: []
      },
      price_max: 0,
      price_min: 0
    }
  }),
  getters: {
    getOffersPrice(state) {
      return {
        min: state.meta.price_min || 0,
        max: state.meta.price_max || 0
      }
    },

    offers(state) {
      return state.offersData.filter((offer: ProductOfferData) => offer.in_stock && offer.is_active)
    },

    offersDataLength(state) {
      return state.offersData.length
    },

    fetchingContent(state) {
      return state.fetching && !state.offersData.length
    }
  },
  actions: {
    async getOffers({ productUUID, query = {} }: { productUUID: string, query: Partial<QueryParamsList> }) {
      return new Promise(async (resolve, reject) => {
        const { offset = 0, limit = OFFERS_LIMIT } = query
        const isConcat = Boolean(offset)

        if (!isConcat) {
          this.$reset()
        }

        this.fetching = true

        const $api = useNuxtApp().$api
        
        $api<ServerListResponse<ProductOfferData, ProductOffersMeta>>(
          `/api/catalog/products/${encodeURIComponent(productUUID)}/offers/?${buildQueryString({ limit, offset })}`
        )
          .then((response) => {
            this.getDeliveriesOffers(response.results)
  
            if (isConcat) {
              this.offersData = this.offersData.concat(response.results)
              this.offersCount = response.count
              this.offersConditions = response.meta.condition_key.items
              this.meta = response.meta
            } else {
              this.offersData = response.results
              this.offersConditions = response.meta.condition_key.items
              this.offersCount = response.count
              this.meta = response.meta
            }
  
            resolve(response)
          })
          .catch((e) => {
            this.$reset()

            reject(e)
          })
          .finally(() => {
            this.fetching = false
          })
      })
    },

    async getDeliveriesOffers(offers: ProductOfferData[]) {
      const productStore = useProductStore()

      try {
        // унікальні ключі uuid доставки оферів
        const deliveriesUuids = new Set(
          offers.map((offer) => offer.delivery.uuid)
        )
        const promisesDeliveries: Promise<any>[] = []

        deliveriesUuids.forEach((uuid) => {
          promisesDeliveries.push(productStore.getOfferDelivery(uuid))
        })

        await Promise.all(promisesDeliveries)
      } finally { /* empty */ }
    }
  }
})
